import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TariffService } from '@api/services/tariff.service';
import { TicketPeriod } from '@api/models/TariffEditorApi/Contract/ticket-period';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { PeriodsService } from '@api/services/periods.service';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { tap } from 'rxjs/operators';
import { AddTariffTicketPeriodRequest } from '@api/models/TariffEditorApi/Contract/add-tariff-ticket-period-request';
import { AddTariffTicketPeriodResponse } from '@api/models/TariffEditorApi/Contract/add-tariff-ticket-period-response';
import { SpecialDatesService } from '@api/services/special-dates.service';
import {
    SetTariffTicketPeriodNameRequest,
} from '@api/models/TariffEditorApi/Contract/SpecDate/set-tariff-ticket-period-name-request';
import {
    GetTariffTicketPeriodDataResponse,
} from '@api/models/TariffEditorApi/Contract/get-tariff-ticket-period-data-response';
import {
    AddTariffTicketPeriodDateRequest,
} from '@api/models/TariffEditorApi/Contract/add-tariff-ticket-period-date-request';
import { CustomRxState } from '@core/state/rx.state';


interface ISpecificValidityPeriodsState {
    loading: Record<StartWithUnit, boolean>;
    periods: TicketPeriod[];
    period: GetTariffTicketPeriodDataResponse;
}

interface SpecificValidityPeriodsAction {
    requestPeriods: TicketPeriod[];
    requestPeriod: GetTariffTicketPeriodDataResponse;
}

@Injectable()
export class SpecificValidityPeriodsState extends CustomRxState<ISpecificValidityPeriodsState> {
    readonly #tariffService: TariffService = inject(TariffService);
    readonly #periodsService: PeriodsService = inject(PeriodsService);
    readonly #specialDatesService: SpecialDatesService = inject(SpecialDatesService);
    readonly #actions: RxActions<SpecificValidityPeriodsAction> = new RxActionFactory<SpecificValidityPeriodsAction>().create();

    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly periods$: Observable<TicketPeriod[]> = this.select('periods');
    public readonly period$: Observable<GetTariffTicketPeriodDataResponse> = this.select('period');

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();

        this.requestPeriods();
    }

    public requestPeriods(): void {
        this.hold(
            this.#tariffService.apiTariffListTicketPeriodGet(),
            this.#actions.requestPeriods,
        );
    }

    public requestPeriod(ttpId: number): void {
        this.hold(
            this.#periodsService.apiTariffGetTariffTicketPeriodDataGet({ ttpId }),
            this.#actions.requestPeriod,
        );
    }

    public clearPeriodData(): void {
        this.#actions.requestPeriod(null);
    }

    public createPeriod(body: AddTariffTicketPeriodRequest): Observable<AddTariffTicketPeriodResponse> {
        return this.#periodsService.apiTariffAddTariffTicketPeriodPost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditSpecificValidityPeriod),
            tap(() => this.requestPeriods()),
        );
    }

    public editPeriod(body: SetTariffTicketPeriodNameRequest): Observable<void> {
        return this.#specialDatesService.apiTariffSetTariffTicketPeriodNamePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditSpecificValidityPeriod),
            tap(() => this.requestPeriods()),
        );
    }

    public deletePeriod(ttpId: number): Observable<void> {
        return this.#periodsService.apiTariffRemoveTariffTicketPeriodDelete({ ttpId }).pipe(
            startWithLoading(this, StartWithUnit.DeleteSpecificValidityPeriod),
            tap(() => this.requestPeriods()),
        );
    }

    public createPeriodInterval(body: AddTariffTicketPeriodDateRequest): Observable<void> {
        return this.#periodsService.apiTariffAddTariffTicketPeriodDatePost({ body }).pipe(
            startWithLoading(this, StartWithUnit.AddOrEditSpecificValidityPeriodInterval),
            tap(() => this.requestPeriod(body.ttpId)),
        );
    }

    public deletePeriodInterval(ttpId: number, dateFromArr: string[]): Observable<void> {
        return this.#periodsService.apiTariffRemoveTariffTicketPeriodDateDelete({ ttpId, dateFromArr }).pipe(
            startWithLoading(this, StartWithUnit.DeleteSpecificValidityPeriodInterval),
            tap(() => this.requestPeriod(ttpId)),
        );
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            periods: null,
            period: null,
        });
    }

    private connectSelectors(): void {
        this.connect('periods', this.#actions.requestPeriods$);
        this.connect('period', this.#actions.requestPeriod$);
    }
}
